import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';

import { Button, CardContent, Input, Label } from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  FormMessage,
  PasswordInput,
  errorToFormIssue,
  signInEmail,
} from '@virtual-care/ui';

import { auth, getIdentity } from '../firebase.tsx';

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'shared.validation.emailRequired' })
    .email({ message: 'shared.validation.emailInvalid' }),
  password: z
    .string()
    .min(1, { message: 'shared.validation.passwordRequired' }),
});

export default function B2BLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        try {
          log.info({ action: 'b2b_login' });
          await signInEmail(auth, props.value.email, props.value.password);
          await getIdentity();
          await navigate('/');
          return void 0;
        } catch (error) {
          log.error({ action: 'b2b_login', error });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      center
      title={t('auth.b2bLogin.title')}
      subtitle={t('auth.b2bLogin.subtitle')}
    >
      <CardContent>
        <form
          className="mt-6 mb-4 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="email"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email">
                  {t('auth.common.email')}
                </Label>
                <Input
                  id="email"
                  dir="ltr"
                  type="text"
                  placeholder={t('auth.common.emailPlaceholder')}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email-password">
                  {t('auth.common.password')}
                </Label>
                <PasswordInput
                  id="email-password"
                  dir="ltr"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full cursor-pointer"
              >
                {isSubmitting
                  ? t('auth.common.signingIn')
                  : t('auth.common.signin')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
          <div className="flex flex-col space-y-4">
            <Link
              to="/b2b-forgot-password"
              className="text-muted-foreground text-sm"
            >
              {t('auth.b2bLogin.forgotPassword')}
            </Link>
          </div>
        </form>
      </CardContent>
    </AuthLayout>
  );
}
